import { useState, useEffect, useCallback } from "react";
import {
  ImageIcon,
  Plus,
  Trash2,
  Pencil,
  X,
  Search,
  Tag,
  Frame,
} from "lucide-react";
import adminApi from "@/lib/adminApi";
import { AdminSelect } from "./AdminSelect";
import ImageUploadField from "./ImageUploadField";

const SCENE_TYPES = [
  { value: "room_scene", label: "Room Scene" },
  { value: "wall_frame", label: "Wall / Frame" },
  { value: "close_up", label: "Close-up" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "flat_lay", label: "Flat Lay" },
  { value: "other", label: "Other" },
];

const PRODUCT_TYPES = [
  { value: "canvas", label: "Canvas" },
  { value: "wall_mural", label: "Wall Mural" },
];

const EMPTY_FORM = {
  name: "",
  image_url: "",
  scene_type: "room_scene",
  compatible_types: [],
  tags: [],
  placement_defaults: null,
};

function LibraryForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const [tagInput, setTagInput] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => {
    setForm((f) => ({
      ...f,
      compatible_types: f.compatible_types.includes(t)
        ? f.compatible_types.filter((x) => x !== t)
        : [...f.compatible_types, t],
    }));
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) {
      update("tags", [...form.tags, t]);
    }
    setTagInput("");
  };

  const removeTag = (t) => update("tags", form.tags.filter((x) => x !== t));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.image_url.trim()) return;
    onSave(form);
  };

  const labelCls = "block text-[10px] text-white/40 uppercase tracking-wider mb-1";
  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/10 text-white text-xs rounded px-3 py-2.5 outline-none transition-colors hover:border-white/20 focus:border-[#CBA052]/40 focus:ring-1 focus:ring-[#CBA052]/20";

  return (
    <form
      onSubmit={handleSubmit}
      className="mb-5 p-4 border border-[#CBA052]/20 bg-[#0a0a0a] rounded space-y-4"
      data-testid="mockup-library-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className={labelCls}>Name<span className="text-red-400 ml-0.5">*</span></span>
          <input
            className={inputCls}
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="e.g. Modern Living Room"
            required
            data-testid="mockup-library-name"
          />
        </label>
        <div className="block">
          <ImageUploadField
            value={form.image_url}
            onChange={(url) => update("image_url", url)}
            label="Background Image"
            category="mockup-library"
            group="Mockup Library"
            testIdPrefix="mockup-library-image"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block">
          <span className={labelCls}>Scene Type</span>
          <AdminSelect
            value={form.scene_type}
            onChange={(e) => update("scene_type", e.target.value)}
            size="md"
            data-testid="mockup-library-scene"
          >
            {SCENE_TYPES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </AdminSelect>
        </label>

        <div>
          <span className={labelCls}>Compatible With</span>
          <div className="flex gap-2 mt-1">
            {PRODUCT_TYPES.map((pt) => (
              <button
                key={pt.value}
                type="button"
                onClick={() => toggleType(pt.value)}
                className={`text-[10px] font-bold px-3 py-1.5 rounded border transition-colors ${
                  form.compatible_types.includes(pt.value)
                    ? "bg-[#CBA052]/20 border-[#CBA052]/40 text-[#CBA052]"
                    : "border-white/10 text-white/40 hover:text-white/60"
                }`}
                data-testid={`mockup-compat-${pt.value}`}
              >
                {pt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className={labelCls}>Tags</span>
          <div className="flex gap-1.5">
            <input
              className={`${inputCls} flex-1`}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              placeholder="Add tag..."
              data-testid="mockup-library-tag-input"
            />
            <button
              type="button"
              onClick={addTag}
              className="text-[10px] border border-white/10 text-white/50 px-2 rounded hover:text-white transition-colors"
            >
              +
            </button>
          </div>
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {form.tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 text-[9px] bg-white/[0.04] border border-white/10 text-white/60 px-2 py-0.5 rounded"
                >
                  {t}
                  <X size={8} className="cursor-pointer hover:text-red-400" onClick={() => removeTag(t)} />
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-white/40 border border-white/10 px-4 py-1.5 rounded hover:text-white transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving || !form.name.trim() || !form.image_url.trim()}
          className="text-xs font-bold text-black bg-[#CBA052] px-4 py-1.5 rounded hover:opacity-90 disabled:opacity-50"
          data-testid="mockup-library-save"
        >
          {saving ? "Saving..." : initial?.id ? "Update" : "Add to Library"}
        </button>
      </div>
    </form>
  );
}

export default function MockupLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filterScene, setFilterScene] = useState("");
  const [filterType, setFilterType] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterScene) params.set("scene_type", filterScene);
      if (filterType) params.set("product_type", filterType);
      const res = await adminApi.get(`/api/admin/templates/mockup-library?${params}`);
      setItems(res.data.items || []);
    } catch (err) {
      console.error("[MockupLibrary] Failed to fetch items:", err);
    }
    setLoading(false);
  }, [filterScene, filterType]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await adminApi.put(`/api/admin/templates/mockup-library/${editing.id}`, form);
      } else {
        await adminApi.post("/api/admin/templates/mockup-library", form);
      }
      setShowForm(false);
      setEditing(null);
      fetchItems();
    } catch (err) {
      console.error("[MockupLibrary] Save failed:", err);
    }
    setSaving(false);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the library?`)) return;
    try {
      await adminApi.delete(`/api/admin/templates/mockup-library/${item.id}`);
      fetchItems();
    } catch (err) {
      console.error("[MockupLibrary] Delete failed:", err);
    }
  };

  const openEdit = (item) => {
    setEditing(item);
    setShowForm(true);
  };

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  const filtered = items.filter((item) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      (item.tags || []).some((t) => t.includes(q)) ||
      item.scene_type.includes(q)
    );
  });

  return (
    <div className="border border-white/[0.08] bg-[#060606]" data-testid="mockup-library">
      {/* Header */}
      <div className="p-5 border-b border-white/[0.06] flex items-center gap-3 flex-wrap">
        <Frame size={16} className="text-[#CBA052]" />
        <h3 className="text-sm font-bold text-white">Shared Mockup Library</h3>
        <span className="text-[10px] text-white/30">{items.length} backgrounds</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90"
            data-testid="mockup-library-add-btn"
          >
            <Plus size={12} /> Add Background
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Form */}
        {showForm && (
          <LibraryForm
            initial={editing || EMPTY_FORM}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            saving={saving}
          />
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/20" />
            <input
              className="w-full bg-[#0a0a0a] border border-white/10 text-white text-xs rounded pl-7 pr-3 py-1.5 outline-none transition-colors hover:border-white/20 focus:border-[#CBA052]/40"
              placeholder="Search backgrounds..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mockup-library-search"
            />
          </div>
          <AdminSelect value={filterScene} onChange={(e) => setFilterScene(e.target.value)} className="w-auto" data-testid="mockup-library-filter-scene">
            <option value="">All scenes</option>
            {SCENE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </AdminSelect>
          <AdminSelect value={filterType} onChange={(e) => setFilterType(e.target.value)} className="w-auto" data-testid="mockup-library-filter-type">
            <option value="">All types</option>
            {PRODUCT_TYPES.map((pt) => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
          </AdminSelect>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block w-5 h-5 border-2 border-[#CBA052]/30 border-t-[#CBA052] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-white/[0.08] rounded" data-testid="mockup-library-empty">
            <ImageIcon size={24} className="mx-auto text-white/10 mb-3" />
            <p className="text-xs text-white/30 mb-1">
              {items.length === 0
                ? "No backgrounds in the library yet"
                : "No backgrounds match the current filters"}
            </p>
            {items.length === 0 && (
              <p className="text-[10px] text-white/20">
                Add room scenes, wall frames, and lifestyle photos for mockup generation.
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="mockup-library-grid">
            {filtered.map((item) => (
              <div
                key={item.id}
                className="group relative border border-white/[0.06] bg-[#0a0a0a] rounded overflow-hidden hover:border-white/15 transition-colors"
                data-testid={`mockup-item-${item.id}`}
              >
                {/* Thumbnail */}
                <div className="aspect-[3/2] bg-black/50 relative">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                  {/* Overlay actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="p-1.5 bg-white/10 rounded hover:bg-white/20 transition-colors"
                      data-testid={`mockup-edit-${item.id}`}
                    >
                      <Pencil size={12} className="text-white" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-1.5 bg-red-500/20 rounded hover:bg-red-500/30 transition-colors"
                      data-testid={`mockup-delete-${item.id}`}
                    >
                      <Trash2 size={12} className="text-red-400" />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-2.5">
                  <p className="text-[11px] font-medium text-white truncate">{item.name}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[9px] text-white/30 bg-white/[0.04] border border-white/[0.06] px-1.5 py-0.5 rounded">
                      {SCENE_TYPES.find((s) => s.value === item.scene_type)?.label || item.scene_type}
                    </span>
                    {(item.compatible_types || []).map((ct) => (
                      <span key={ct} className="text-[9px] text-[#CBA052]/60 bg-[#CBA052]/[0.06] border border-[#CBA052]/10 px-1.5 py-0.5 rounded">
                        {ct}
                      </span>
                    ))}
                  </div>
                  {(item.tags || []).length > 0 && (
                    <div className="flex items-center gap-1 mt-1.5">
                      <Tag size={8} className="text-white/15" />
                      <p className="text-[9px] text-white/20 truncate">
                        {item.tags.join(", ")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
